/**
 * ## Socket listener
 *
 * This module registers socket event handlers for low-level events
 * like `connected` and `disconnected`, and also for domain-specific
 * events related to data updates, synchronization, and offline status.
 *
 * @module client/socket
 */

"use strict";

const io = require("socket.io-client");
const state = require("../state");
const dal = require("../dal");
const offline = require("../offline");
const config = require("../../config/client.js");
const domains = {};

/**
 * Configure and connect to Socket.IO server
 *
 * Sets up the socket listeners for update and sync,
 * and passes events to the kdClient
 * for direct app interaction
 *
 * @param {Object} kd - KDClient instance
 * @param {socket.io.Socket} [existingSocket] - Existing socket instance
 *                                             used for testing
 * @returns {socket.io.Socket} _socket - Client socket instance
 */
const configure = (kd, existingSocket) => {
  let _socket;
  if (existingSocket) {
    _socket = existingSocket;
    return existingSocket;
  }

  let url;
  if (!kd.localstore.data.config.sio.port) {
    url = kd.localstore.data.config.sio.host;
  } else {
    url = `${kd.localstore.data.config.sio.host}:${kd.localstore.data.config.sio.port}`;
  }

  const query = {
    query: {
      token: kd.localstore.data.credentials.token,
      room: kd.localstore.data.config.room,
      service: kd.localstore.data.config.service,
      api: JSON.stringify(kd.localstore.data.config.api),
    },
  };

  const options = Object.assign(query, kd.localstore.data.config.sio.options);
  _socket = io.connect(
    `${url}/${kd.localstore.data.credentials.site_id}`,
    options
  );

  _socket.on("connected", (claims) => {
    connected_event(claims, kd);
  });

  config.domains.forEach((domain) => {
    domains[domain] = false;
  });

  return _socket;
};

/**
 * Fires when an update is event is received on any valid domain.
 *
 * @param {string} domain - name of the domain receiving the event
 * @param {Object} proposed - json change object
 * @param {Object} kd - KDClient instance
 * @param {Object} kd._socket - live currently connected socket
 * @param {Object} kd.db - current client db
 * @param {Object} kd.localstore - current client local data
 */

const state_update = (domain, proposed, kd) => {
  dal
    .update(proposed, kd.localstore, kd._socket, kd.db, kd)
    .then((proposed) => {
      // send back change object only, not full merged result object
      kd.emit(`${domain}.update`, proposed);
    })
    .catch((err) => {
      //kd.emit(`${domain}.update`, err);
      console.log(err);
    });
  return true;
};

/**
 * Socket `disconnected` event handler
 *
 * Update local offline status and reset event handlers
 *
 * @param {Object} payload -
 * @param {Object} kd - KDClient instance
 * @param {Object} kd._socket - live currently connected socket
 * @param {Object} kd.localstore - current client local data
 */

const disconnect_event = (payload, kd) => {
  kd.localstore.data.offline = true;
  kd.localstore.data.lastsync = new Date().getTime() - 20000;
  kd.emit("_socket.disconnect", payload);

  kd._socket.removeAllListeners();
  kd._socket.on("connected", (claims) => {
    connected_event(claims, kd);
  });

  return true;
};

/**
 * Fires when a sync is event is received on any valid domain.
 *
 * @param {string} domain - name of the domain receiving the event
 * @param {Object} syncdata - json change object
 * @param {Object} kd - KDClient instance
 * @param {Object} kd.db - current client db
 */
const state_initialize = (domain, syncdata, kd) => {
  let wait;
  syncdata.data.forEach((doc) => {
    state
      .update(doc, kd.db)
      .then((merged) => {
        kd.emit(`${domain}.initialize`, merged);
        if (wait) {
          clearTimeout(wait);
        }
        domains.activity = true;
        domains.reservation = true;
        domains.note = true;
        domains[domain] = true;
        wait = setTimeout(() => {
          if (allTrue(domains)) {
            clearTimeout(wait);
            domains[domain] = false;
            kd.emit("ready", {});
          }
        }, 50);
      })
      .catch((err) => {
        kd.emit(`${domain}.update`, err);
      });
  });
  return true;
};

const allTrue = (obj) => {
  for (const o in obj) {
    if (!obj[o]) return false;
  }
  return true;
};

/**
 * Socket `connected` event handler
 *
 * Setup the client event handlers depending upon the service specified
 * e.g. 'state', 'dataservices', 'coreservices'
 * The client will disconnect if returned credentials don't match the site_id
 * found in the credentials.
 *
 * @param {Object} claims - Server-verified JWT claims
 * @param {Object} kd - KDClient instance
 * @param {Object} kd._socket - live currently connected socket
 * @param {Object} kd.db - current client db
 * @param {Object} kd.localstore - current client local data
 */

const connected_event = (claims, kd) => {
  // Bind disconnect handler
  kd._socket.on("disconnect", (payload) => {
    disconnect_event(payload, kd);
  });

  if (!claims.authorized) {
    kd.emit("disconnect", { authorized: false });
    return true;
  }

  kd.localstore.data.offline = false;
  kd.localstore.data.credentials.claims = claims;
  kd.localstore.data.credentials.site_id = claims.site_id;
  kd.localstore.data.credentials.user_id = claims.user_id;
  kd.localstore.data.credentials.room = claims.room;
  kd.localstore.data.credentials.service = claims.service;

  // For each Socket.IO room, setup event handlers to emit events to the
  // client app and handle any room-specific requirements
  switch (kd.localstore.data.credentials.room) {
    case "state": {
      // aux channel
      kd._socket.on("aux", (data) => {
        kd.emit("aux", data);
      });

      // Bind event handlers for each defined domain in the schema
      config.domains.forEach((domain) => {
        kd._socket.on(`${domain}.update`, (proposed) => {
          state_update(domain, proposed, kd);
        });
        kd._socket.on(`${domain}.initialize`, (syncdata) => {
          state_initialize(domain, syncdata, kd);
        });

        kd._socket.on(`${domain}.error`, (errordata) => {
          console.log(`${domain}.error`, errordata);
        });
      });

      // Emit an update to the server for each domain object that is marked as offline

      const dataobj = {
        lastsync: kd.localstore.data.lastsync,
      };

      offline.initialize(kd._socket, kd.db).then((success) => {
        if (success) {
          config.domains.forEach((domain) => {
            dataobj.domain = domain;
            dal.initialize(dataobj, kd.localstore, kd._socket, kd.db);
            kd.localstore.data.lastsync = new Date().getTime() - 20000;
          });
        }
      });

      break;
    }
    default:
      break;
  }

  kd.emit("_socket.connected", claims);
  return true;
};

module.exports = {
  configure,
  connected_event,
  disconnect_event,
  state_update,
  state_initialize,
};



/** WEBPACK FOOTER **
 ** ./src/client/socket/index.js
 **/